import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import get from 'lodash/get';
import { useMutation } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@alefeducation/ui-elements-library-core';
import submissionResponse from '../../apollo/mutations/submissionResponse.graphql';
import submissionsReturn from '../../apollo/mutations/submissionsReturn.graphql';
import { buildSubmissionsReturnInput } from '../../helpers/buildSubmissionsReturnInput';
import Container from '../common/Container/Container';
import {
  InfoWrapper,
  Title,
} from '../common/AssignmentPageLayout/AssignmentPageLayout';
import AvatarRenderer from '../common/AvatarRenderer/AvatarRenderer';
import { GradeScore } from '../AssignmentClassDashboard/GradeScore';
import Spinner from '../common/Spinner/Spinner';
import {
  CONVERSION_STATUS,
  useAssignmentAttachmentState,
} from '../AssignmentSubmission/AssignmentAttachmentContext';
import { rtl } from '../../utils/language';
import { afterSubmissionResponseReturn } from '../../helpers/afterSubmissionResponseReturn';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import fireNotification from '../../utils/fireNotification';
import {
  isAssignmentGraded,
  isAssignmentReturned,
  isAssignmentSubmitted,
} from '../../utils/assignmentStatusUtils';
import uploadResultToAttachmentInput from '../../utils/uploadResultToAttachmentInput';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import StarsAward from '../common/StarsAward/StarsAward';
import {
  useStudentSubmissionDispatch,
  useStudentSubmissionState,
} from './StudentSubmissionPageContext';
import { ControlButtons } from './ControlButtons';
import ReturnSubmissionConfirmationModal from './ReturnSubmissionConfirmationModal';
import { GradeDescription } from './GradeDescription';
import Feedback from './Feedback';
import { classAssignmentSubmissionPageQuery } from './constants';

const AssignmentTitle = styled(Title)`
  font-family: var(--font-family-signika);
  color: var(--river-bed);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  word-wrap: break-word;
`;

const SectionWrapper = styled(InfoWrapper)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px !important;
`;

const StudentInfoWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const StudentAvatar = styled.div`
  max-width: 89px;
  margin-right: 5px;
  margin-left: 0;
  ${rtl`
      margin-right: 0;
      margin-left: 5px;
    `};
`;

const StudentName = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-right: 15px;
  flex: 1;

  & > div {
    color: var(--river-bed);
    width: 100%;
    font-size: 18px;
    font-family: var(--font-family-signika);
    font-weight: 400;
    line-height: 22px;
    padding-top: 20px;
    box-sizing: border-box;
    word-wrap: break-word;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const StudentScore = styled(GradeScore)`
  padding: 1px 2px 1px 2px;
  max-width: 80px;
  display: flex;
  transform: none;
  left: unset;

  border-radius: 6px;
  border: solid 1px var(--silver);
  background-color: transparent;
  font-size: var(--font-size-small);

  & > input {
    width: 35px;
    display: inline-block;

    border-radius: unset;
    border: none;
    background-color: inherit;
    padding: unset;
    text-align: center;
    align-items: center;

    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  & > span.max-score {
    position: static;
    top: unset;
    transform: unset;
    right: unset;
  }
`;

const GradeSection = styled.div`
  font-family: var(--font-family-signika) !important;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  & > span {
    font-size: 12px;
  }

  & > div {
    align-items: flex-start;
    font-family: var(--font-family-signika);
  }
`;

const GradeLabel = styled.div`
  color: black;
  font-size: 16px;
  font-family: var(--font-family-signika);
  font-weight: 300;
  word-wrap: break-word;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const StarsAwardWrapper = styled.div`
  margin-bottom: 5px;
  display: flex;
  align-items: flex-start;
`;

export const SubmissionInfo = ({ onCancel, onSubmissionsReturn }) => {
  const {
    assignmentParams: { assignmentInstanceId, mloId, finishExperienceContext },
    assignmentData,
  } = useContext(AssignmentContext);
  const { i18n, t } = useTranslation();
  const langCode = i18n.language;
  const shouldTriggerSavedNotification = React.useRef(true);
  const responseSubmissionMutationOptions = {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
    onCompleted: () =>
      shouldTriggerSavedNotification.current &&
      fireNotification('TeacherSubmissionSaveSuccess'),
    refetchQueries: [classAssignmentSubmissionPageQuery],
  };
  const returnSubmissionMutationOptions = {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
    onCompleted: data => {
      onSubmissionsReturn(data.submissionsReturn);
      fireNotification('TeacherSubmissionReturnSingleSuccess');
      shouldTriggerSavedNotification.current = true;
    },
    refetchQueries: [classAssignmentSubmissionPageQuery],
  };
  const [
    responseSubmissionMutation,
    { loading: isSubmissionInProgress },
  ] = useMutation(submissionResponse, responseSubmissionMutationOptions);
  const [
    returnSubmissionMutation,
    { loading: isReturningInProgress },
  ] = useMutation(submissionsReturn, returnSubmissionMutationOptions);

  const {
    selectedStudent,
    assignment,
    teacherFeedback,
    studentScore,
    isReturnSubmissionConfirmationOpen,
    isSaveButtonEnabled,
  } = useStudentSubmissionState();
  const dispatch = useStudentSubmissionDispatch();
  const { attachment, conversionStatus } = useAssignmentAttachmentState();
  const hasAllTheData = !!(selectedStudent && assignment);
  const [firstName, lastName] = hasAllTheData
    ? selectedStudent.student.name.split(' ')
    : [];
  const isActiveStudentInTheClass = !(
    selectedStudent &&
    selectedStudent.student &&
    !selectedStudent.student.active
  );
  const isReturned =
    hasAllTheData &&
    selectedStudent.submission &&
    isAssignmentReturned(selectedStudent.submission.status);
  const isGraded =
    hasAllTheData &&
    selectedStudent.submission &&
    isAssignmentGraded(selectedStudent.submission.status);
  const isSubmitted =
    hasAllTheData &&
    selectedStudent.submission &&
    isAssignmentSubmitted(selectedStudent.submission.status);
  const [isGradeFieldFocused, setisGradeFieldFocused] = React.useState(false);

  const isReturnButtonEnabled =
    isActiveStudentInTheClass &&
    !isReturningInProgress &&
    !isReturned &&
    !isGradeFieldFocused &&
    conversionStatus !== CONVERSION_STATUS.inProgress &&
    (Number.isInteger(studentScore) ||
      (assignment && !assignment.gradeable && teacherFeedback)) &&
    studentScore <= assignment.maxScore;
  const [isFeedbackEditable, setisFeedbackEditable] = React.useState(false);
  const referrerId = assignmentInstanceId || mloId;

  const onSave = e => {
    e.preventDefault();
    if (hasAllTheData) {
      if (studentScore > assignment.maxScore) {
        fireNotification(
          'evaluatedExceedTheMaxScoreError',
          langCode,
          t("The student's score cannot exceed the max score."),
        );
        return undefined;
      }

      const studentAttachmentPath = get(
        selectedStudent,
        'submission.attachment.path',
      );
      const renderedAttachmentPath = get(attachment, 'path');
      const didTeacherReplacedAttachment =
        studentAttachmentPath !== renderedAttachmentPath;

      return responseSubmissionMutation({
        variables: {
          id: selectedStudent.submission
            ? selectedStudent.submission.id
            : undefined,
          submissionTeacherInput: !selectedStudent.submission
            ? {
                assignmentId: assignmentData.assignmentId,
                referrerId,
                studentId: selectedStudent.student.id,
              }
            : undefined,
          input: {
            score: studentScore,
            comment: teacherFeedback || null,
            attachment: didTeacherReplacedAttachment
              ? uploadResultToAttachmentInput(attachment)
              : null,
          },
        },
        update: afterSubmissionResponseReturn({
          isReturning: false,
          attachmentRequired: assignment.attachmentRequired,
          commentRequired: assignment.commentRequired,
        }),
      });
    }

    return undefined;
  };

  const onReturn = async e => {
    e.preventDefault();
    shouldTriggerSavedNotification.current = false;
    if (!hasAllTheData) return;
    const result = await onSave(e);
    const submissionId =
      get(result, 'data.submissionResponse.id') ||
      get(selectedStudent, 'submission.id');
    await returnSubmissionMutation({
      variables: {
        input: buildSubmissionsReturnInput(
          [submissionId],
          finishExperienceContext,
        ),
      },
      update: afterSubmissionResponseReturn({
        isReturning: true,
        attachmentRequired: assignment.attachmentRequired,
        commentRequired: assignment.commentRequired,
      }),
    });
  };

  useEffect(
    () => {
      dispatch({
        type: 'calculateSaveButtonStatus',
        payload: {
          isReturningInProgress,
          isReturned,
          isGradeFieldFocused,
          conversionStatus,
          attachment,
        },
      });
    },
    [
      hasAllTheData,
      isReturned,
      selectedStudent,
      studentScore,
      teacherFeedback,
      isGradeFieldFocused,
      attachment,
      conversionStatus,
      isReturningInProgress,
    ],
  );

  useEffect(
    () => {
      setisFeedbackEditable(false);
    },
    [selectedStudent && selectedStudent.student.id],
  );

  useEffect(
    () => {
      if (
        isActiveStudentInTheClass &&
        hasAllTheData &&
        !isReturned &&
        !teacherFeedback
      ) {
        setisFeedbackEditable(true);
      }
    },
    [isActiveStudentInTheClass, isReturned, teacherFeedback, hasAllTheData],
  );
  return (
    <ThemeProvider>
      <Container className="assignmentSubmission-container assignmentSubmission-container__column teacherView">
        {!hasAllTheData ? (
          <Spinner isCentered={true} testId="assignmentSpinner" />
        ) : (
          <>
            <AssignmentTitle>{assignment.title}</AssignmentTitle>
            <SectionWrapper>
              <StudentInfoWrapper
                className="usprivacy"
                data-recording-sensitive
              >
                <StudentAvatar>
                  <AvatarRenderer src={selectedStudent.student.avatar} />
                </StudentAvatar>
                <StudentName>
                  <div>
                    {firstName} {lastName}
                  </div>
                </StudentName>
              </StudentInfoWrapper>

              <GradeSection data-testid={'student-grade-section'}>
                {assignment.maxStars && (
                  <StarsAwardWrapper>
                    <StarsAward
                      max={assignment.maxStars}
                      earned={
                        selectedStudent.submission &&
                        selectedStudent.submission.response &&
                        selectedStudent.submission.response.stars
                      }
                    />
                  </StarsAwardWrapper>
                )}
                {assignment.gradeable && <GradeLabel>{t('Grade')}</GradeLabel>}
                {assignment.gradeable && (
                  <StudentScore
                    submission={selectedStudent.submission}
                    isSubmitted={isSubmitted}
                    attachmentRequired={assignment.attachmentRequired}
                    isReturned={isReturned}
                    response={{ score: studentScore, comment: teacherFeedback }}
                    maxScore={assignment.maxScore}
                    onMutationCompleted={payload =>
                      dispatch({ type: 'score', payload })
                    }
                    student={selectedStudent.student}
                    isGraded={isGraded}
                    deferredSave={true}
                    isFocused={isGradeFieldFocused}
                    setFocus={setisGradeFieldFocused}
                    isActiveStudentInTheClass={isActiveStudentInTheClass}
                    commentRequired={assignment.commentRequired}
                    isStudentSubmission={true}
                  />
                )}
                <GradeDescription
                  selectedStudent={selectedStudent}
                  isReturned={isReturned}
                  isGraded={isGraded}
                />
              </GradeSection>
            </SectionWrapper>
            <Feedback
              isReturned={isReturned}
              isSubmissionInProgress={isSubmissionInProgress}
              teacherFeedback={teacherFeedback || ''}
              isFeedbackEditable={isFeedbackEditable}
              setisFeedbackEditable={setisFeedbackEditable}
              isActiveStudentInTheClass={isActiveStudentInTheClass}
              isSubmitted={isSubmitted}
              attachmentRequired={assignment.attachmentRequired}
              commentRequired={assignment.commentRequired}
            />
            <ControlButtons
              isReturnButtonEnabled={isReturnButtonEnabled}
              isSaveButtonEnabled={isSaveButtonEnabled}
              isSubmissionInProgress={isSubmissionInProgress}
              onCancel={onCancel}
              onSave={onSave}
              onReturn={() =>
                dispatch({ type: 'openReturnSubmissionConfirmation' })
              }
            />
            {isReturnSubmissionConfirmationOpen && (
              <ReturnSubmissionConfirmationModal onConfirm={onReturn} />
            )}
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

SubmissionInfo.propTypes = {
  onCancel: PropTypes.func.isRequired,
  assignmentId: PropTypes.string.isRequired,
  assignmentType: PropTypes.string.isRequired,
  onSubmissionsReturn: PropTypes.func.isRequired,
};
